import { doc, getDoc, setDoc, updateDoc, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Fetches the current Game of the Day ID and date from Firestore
 * @returns {Promise<Object>} Object containing gameId and date, or null if none is set
 */
export const getFeaturedGameData = async () => {
  try {
    const featuredGameDocRef = doc(db, 'featuredContent', 'gameOfTheDay');
    const docSnap = await getDoc(featuredGameDocRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        gameId: data.gameId || null,
        date: data.date || null
      };
    } else {

      return { gameId: null, date: null };
    }
  } catch (error) {
    console.error('Error fetching featured game data:', error);
    return { gameId: null, date: null };
  }
};

/**
 * Fetches only the current Game of the Day ID from Firestore (for backward compatibility)
 * @returns {Promise<string>} The current featured game ID or null if none is set
 */
export const getFeaturedGameId = async () => {
  try {
    const data = await getFeaturedGameData();
    return data.gameId;
  } catch (error) {
    console.error('Error in getFeaturedGameId:', error);
    return null;
  }
};

/**
 * Fetches the Game of the Day history from Firestore
 * @returns {Promise<Array<string>>} Array of game IDs in the history
 */
export const getGameOfDayHistory = async () => {
  try {
    const featuredGameDocRef = doc(db, 'featuredContent', 'gameOfTheDay');
    const docSnap = await getDoc(featuredGameDocRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.history || [];
    } else {

      return [];
    }
  } catch (error) {
    console.error('Error fetching game history:', error);
    return [];
  }
};

/**
 * Fetches the Game of the Day history from Firestore along with dates if available
 * @returns {Promise<Object>} Object containing history array and historyDates object
 */
export const getGameOfDayHistoryWithDates = async () => {
  try {
    const featuredGameDocRef = doc(db, 'featuredContent', 'gameOfTheDay');
    const docSnap = await getDoc(featuredGameDocRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        history: data.history || [],
        historyDates: data.historyDates || {}
      };
    } else {

      return { history: [], historyDates: {} };
    }
  } catch (error) {
    console.error('Error fetching game history with dates:', error);
    return { history: [], historyDates: {} };
  }
};

/**
 * Fetches the array of games for the Landing page
 * @returns {Promise<Array<string>>} Array of game IDs for the landing page
 */
export const getLandingGames = async () => {
  try {
    const landingGamesDocRef = doc(db, 'featuredContent', 'landingGames');
    const docSnap = await getDoc(landingGamesDocRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.games || [];
    } else {

      return [];
    }
  } catch (error) {
    console.error('Error fetching landing games:', error);
    return [];
  }
};

/**
 * Fetches the array of featured creators
 * @returns {Promise<Array<Object>>} Array of featured creator objects
 */
export const getFeaturedCreators = async () => {
  try {
    const featuredCreatorsDocRef = doc(db, 'featuredContent', 'featuredCreators');
    const docSnap = await getDoc(featuredCreatorsDocRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.creators || [];
    } else {

      return [];
    }
  } catch (error) {
    console.error('Error fetching featured creators:', error);
    return [];
  }
};

/**
 * A hook-compatible version that can be used in React components for Game of the Day
 * @param {Function} setGameData - State setter function for the game data (id and date)
 * @param {Function} setLoading - State setter function for loading state
 * @param {Function} setError - State setter function for error state
 */
export const fetchFeaturedGameData = async (setGameData, setLoading, setError) => {
  try {
    setLoading?.(true);
    const gameData = await getFeaturedGameData();
    setGameData?.(gameData);
    setLoading?.(false);
  } catch (error) {
    console.error('Error in fetchFeaturedGameData:', error);
    setError?.('Failed to fetch featured game');
    setLoading?.(false);
  }
};

/**
 * A hook-compatible version that can be used in React components for Game of the Day ID only
 * @param {Function} setGameId - State setter function for the game ID
 * @param {Function} setLoading - State setter function for loading state
 * @param {Function} setError - State setter function for error state
 */
export const fetchFeaturedGameId = async (setGameId, setLoading, setError) => {
  try {
    setLoading?.(true);
    const gameData = await getFeaturedGameData();
    setGameId?.(gameData.gameId);
    setLoading?.(false);
  } catch (error) {
    console.error('Error in fetchFeaturedGameId:', error);
    setError?.('Failed to fetch featured game');
    setLoading?.(false);
  }
};

/**
 * A hook-compatible version that can be used in React components for Landing Games
 * @param {Function} setGames - State setter function for the games array
 * @param {Function} setLoading - State setter function for loading state
 * @param {Function} setError - State setter function for error state
 */
export const fetchLandingGames = async (setGames, setLoading, setError) => {
  try {
    setLoading?.(true);
    const games = await getLandingGames();
    setGames?.(games);
    setLoading?.(false);
  } catch (error) {
    console.error('Error in fetchLandingGames:', error);
    setError?.('Failed to fetch landing games');
    setLoading?.(false);
  }
};

/**
 * A hook-compatible version that can be used in React components for Featured Creators
 * @param {Function} setCreators - State setter function for the creators array
 * @param {Function} setLoading - State setter function for loading state
 * @param {Function} setError - State setter function for error state
 */
export const fetchFeaturedCreators = async (setCreators, setLoading, setError) => {
  try {
    setLoading?.(true);
    const creators = await getFeaturedCreators();
    setCreators?.(creators);
    setLoading?.(false);
  } catch (error) {
    console.error('Error in fetchFeaturedCreators:', error);
    setError?.('Failed to fetch featured creators');
    setLoading?.(false);
  }
};

/**
 * Removes a game from the Game of the Day history
 * @param {string} gameId - The ID of the game to remove from history
 * @returns {Promise<boolean>} Success status
 */
export const removeGameFromHistory = async (gameId) => {
  try {
    if (!gameId) {
      console.error('No game ID provided for removal');
      return false;
    }

    const featuredGameDocRef = doc(db, 'featuredContent', 'gameOfTheDay');

    // Remove the game ID from history array
    await updateDoc(featuredGameDocRef, {
      history: arrayRemove(gameId),
      updatedAt: new Date().toISOString()
    });


    return true;
  } catch (error) {
    console.error('Error removing game from history:', error);
    return false;
  }
};

/**
 * Fetches the Tip of the Day content from Firestore
 * @returns {Promise<string>} The current tip of the day or null if none is set
 */
export const getTipOfDay = async () => {
  try {
    const tipDocRef = doc(db, 'featuredContent', 'tipOfTheDay');
    const docSnap = await getDoc(tipDocRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.content || null;
    } else {

      return null;
    }
  } catch (error) {
    console.error('Error fetching tip of the day:', error);
    return null;
  }
};

/**
 * Fetches the Quote of the Day content from Firestore
 * @returns {Promise<string>} The current quote of the day or null if none is set
 */
export const getQuoteOfDay = async () => {
  try {
    const quoteDocRef = doc(db, 'featuredContent', 'quoteOfTheDay');
    const docSnap = await getDoc(quoteDocRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.content || null;
    } else {

      return null;
    }
  } catch (error) {
    console.error('Error fetching quote of the day:', error);
    return null;
  }
};

/**
 * Fetches the Fact of the Day content from Firestore
 * @returns {Promise<string>} The current fact of the day or null if none is set
 */
export const getFactOfDay = async () => {
  try {
    const factDocRef = doc(db, 'featuredContent', 'factOfTheDay');
    const docSnap = await getDoc(factDocRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.content || null;
    } else {

      return null;
    }
  } catch (error) {
    console.error('Error fetching fact of the day:', error);
    return null;
  }
};

/**
 * Fetches all daily content (tip, quote, fact) from Firestore
 * @returns {Promise<Object>} Object containing tip, quote, and fact content
 */
export const getAllDailyContent = async () => {
  try {
    const [tip, quote, fact] = await Promise.all([
      getTipOfDay(),
      getQuoteOfDay(),
      getFactOfDay()
    ]);

    return {
      tip,
      quote,
      fact
    };
  } catch (error) {
    console.error('Error fetching all daily content:', error);
    return {
      tip: null,
      quote: null,
      fact: null
    };
  }
};

/**
 * A hook-compatible version that can be used in React components for Tip of the Day
 * @param {Function} setTip - State setter function for the tip content
 * @param {Function} setLoading - State setter function for loading state
 * @param {Function} setError - State setter function for error state
 */
export const fetchTipOfDay = async (setTip, setLoading, setError) => {
  try {
    setLoading?.(true);
    const tip = await getTipOfDay();
    setTip?.(tip);
    setLoading?.(false);
  } catch (error) {
    console.error('Error in fetchTipOfDay:', error);
    setError?.('Failed to fetch tip of the day');
    setLoading?.(false);
  }
};

/**
 * A hook-compatible version that can be used in React components for Quote of the Day
 * @param {Function} setQuote - State setter function for the quote content
 * @param {Function} setLoading - State setter function for loading state
 * @param {Function} setError - State setter function for error state
 */
export const fetchQuoteOfDay = async (setQuote, setLoading, setError) => {
  try {
    setLoading?.(true);
    const quote = await getQuoteOfDay();
    setQuote?.(quote);
    setLoading?.(false);
  } catch (error) {
    console.error('Error in fetchQuoteOfDay:', error);
    setError?.('Failed to fetch quote of the day');
    setLoading?.(false);
  }
};

/**
 * A hook-compatible version that can be used in React components for Fact of the Day
 * @param {Function} setFact - State setter function for the fact content
 * @param {Function} setLoading - State setter function for loading state
 * @param {Function} setError - State setter function for error state
 */
export const fetchFactOfDay = async (setFact, setLoading, setError) => {
  try {
    setLoading?.(true);
    const fact = await getFactOfDay();
    setFact?.(fact);
    setLoading?.(false);
  } catch (error) {
    console.error('Error in fetchFactOfDay:', error);
    setError?.('Failed to fetch fact of the day');
    setLoading?.(false);
  }
};

/**
 * A hook-compatible version that can be used in React components for all daily content
 * @param {Function} setTip - State setter function for the tip content
 * @param {Function} setQuote - State setter function for the quote content
 * @param {Function} setFact - State setter function for the fact content
 * @param {Function} setLoading - State setter function for loading state
 * @param {Function} setError - State setter function for error state
 */
export const fetchAllDailyContent = async (setTip, setQuote, setFact, setLoading, setError) => {
  try {
    setLoading?.(true);
    const content = await getAllDailyContent();
    setTip?.(content.tip);
    setQuote?.(content.quote);
    setFact?.(content.fact);
    setLoading?.(false);
  } catch (error) {
    console.error('Error in fetchAllDailyContent:', error);
    setError?.('Failed to fetch daily content');
    setLoading?.(false);
  }
}; 